import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  Shield,
  Plane,
  Home,
  Car,
  Heart,
  Briefcase,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Sparkles,
  Zap,
  TrendingUp,
  ArrowRight,
  Calendar,
  User,
} from "lucide-react";

const PoliciesDashboard = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [myPolicies, setMyPolicies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const policyTypes = [
    {
      id: "seguro_viajero",
      title: "Seguro de Viajero",
      description: "Protección completa durante tus viajes nacionales e internacionales",
      icon: Plane,
      color: "from-[#218e01] to-[#5fc500]",
      bgColor: "bg-green-50",
      features: [
        "Cobertura médica en el extranjero",
        "Asistencia 24/7",
        "Protección de equipaje",
        "Cancelación de viaje",
      ],
      path: "/polizas/seguro-viajero",
    },
    {
      id: "seguro_hogar",
      title: "Seguro de Hogar",
      description: "Protege tu patrimonio y bienes más valiosos",
      icon: Home,
      color: "from-[#5fc500] to-[#11653a]",
      bgColor: "bg-emerald-50",
      features: [
        "Cobertura de daños materiales",
        "Robo y hurto",
        "Responsabilidad civil",
        "Asistencia en el hogar",
      ],
      path: "/polizas/seguro-hogar",
      comingSoon: true,
    },
    {
      id: "seguro_auto",
      title: "Seguro de Auto",
      description: "Protección integral para tu vehículo",
      icon: Car,
      color: "from-[#11653a] to-[#096365]",
      bgColor: "bg-teal-50",
      features: [
        "Cobertura de daños",
        "Robo total y parcial",
        "Responsabilidad civil",
        "Asistencia en carretera",
      ],
      path: "/polizas/seguro-auto",
    },
    {
      id: "seguro_vida",
      title: "Seguro de Vida",
      description: "Protección financiera para tu familia",
      icon: Heart,
      color: "from-[#096365] to-[#5ba49e]",
      bgColor: "bg-cyan-50",
      features: [
        "Protección familiar",
        "Ahorro e inversión",
        "Enfermedades graves",
        "Invalidez total",
      ],
      path: "/polizas/seguro-vida",
      comingSoon: true,
    },
    {
      id: "seguro_gmm",
      title: "Gastos Médicos Mayores",
      description: "Cobertura médica integral para ti y tu familia",
      icon: Briefcase,
      color: "from-[#5ba49e] to-[#218e01]",
      bgColor: "bg-blue-50",
      features: [
        "Cobertura hospitalaria",
        "Cirugías y procedimientos",
        "Medicamentos",
        "Consultas especializadas",
      ],
      path: "/polizas/gastos-medicos",
      comingSoon: true,
    },
  ];

  useEffect(() => {
    if (user) {
      loadMyPolicies();
    }
  }, [user]);

  const loadMyPolicies = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const policiesRef = collection(db, "policies");
      const q = query(
        policiesRef,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const policiesData = [];
      querySnapshot.forEach((doc) => {
        policiesData.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setMyPolicies(policiesData);
    } catch (error) {
      console.error("Error al cargar pólizas:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      pendiente: {
        icon: Clock,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "Pendiente",
      },
      aprobada: {
        icon: CheckCircle,
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Aprobada",
      },
      rechazada: {
        icon: XCircle,
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Rechazada",
      },
      en_revision: {
        icon: AlertCircle,
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "En Revisión",
      },
    };

    const config = statusConfig[estado] || statusConfig.pendiente;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getPolicyTypeLabel = (tipo) => {
    const type = policyTypes.find((p) => p.id === tipo);
    return type ? type.title : tipo;
  };

  const filteredPolicies = myPolicies.filter((policy) => {
    const matchesSearch =
      getPolicyTypeLabel(policy.tipoPoliza)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      policy.nombreCompleto?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" || policy.estado === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Acceso no autorizado
          </h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg shadow-md">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#218e01] to-[#5fc500] bg-clip-text text-transparent">
                  Pólizas y Seguros
                </h1>
                <p className="text-xs text-slate-600">
                  Protege lo que más te importa
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Mis Pólizas - Compacto */}
        <div className="mb-8 bg-white rounded-xl p-4 shadow-md border border-slate-200/50">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900">Mis Solicitudes</h2>
            <div className="flex gap-2">
              <div className="flex-1 relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
              >
                <option value="all">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="en_revision">En Revisión</option>
                <option value="aprobada">Aprobada</option>
                <option value="rechazada">Rechazada</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="w-8 h-8 border-2 border-[#5fc500] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredPolicies.length > 0 ? (
            <div className="space-y-2">
              {filteredPolicies.slice(0, 3).map((policy) => {
                const policyType = policyTypes.find((p) => p.id === policy.tipoPoliza);
                const Icon = policyType?.icon || FileText;
                return (
                  <div
                    key={policy.id}
                    className="flex items-center justify-between p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`p-1.5 bg-gradient-to-r ${policyType?.color || "from-slate-400 to-slate-600"} rounded-lg flex-shrink-0`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          {getPolicyTypeLabel(policy.tipoPoliza)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {policy.createdAt?.toDate
                            ? policy.createdAt.toDate().toLocaleDateString("es-MX")
                            : "Fecha no disponible"}
                        </p>
                      </div>
                      {getStatusBadge(policy.estado)}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => navigate(`/polizas/edit/${policy.id}`)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                        title="Editar"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/polizas/${policy.id}`)}
                        className="p-1.5 text-[#5fc500] hover:bg-green-50 rounded-lg transition-colors flex-shrink-0"
                        title="Ver detalles"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {filteredPolicies.length > 3 && (
                <button
                  onClick={() => {
                    const element = document.getElementById("tipos-seguros");
                    element?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="w-full text-xs text-[#5fc500] font-semibold py-2 hover:bg-green-50 rounded-lg transition-colors"
                >
                  Ver todas ({filteredPolicies.length})
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500 text-center py-2">
              {searchTerm || filterStatus !== "all"
                ? "No se encontraron pólizas"
                : "No hay solicitudes"}
            </p>
          )}
        </div>

        {/* Tipos de Pólizas */}
        <div id="tipos-seguros" className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Tipos de Seguros Disponibles
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Selecciona el tipo de seguro que necesitas
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {policyTypes.map((policy) => {
              const Icon = policy.icon;
              return (
                <div
                  key={policy.id}
                  className={`group bg-white rounded-2xl sm:rounded-3xl p-6 shadow-lg border border-slate-200/50 hover:shadow-2xl transition-all duration-300 cursor-pointer ${
                    policy.comingSoon ? "opacity-75" : ""
                  }`}
                  onClick={() => !policy.comingSoon && navigate(policy.path)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`p-4 bg-gradient-to-r ${policy.color} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    {policy.comingSoon && (
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">
                        Próximamente
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {policy.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {policy.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {policy.features.slice(0, 3).map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-sm text-slate-700"
                      >
                        <CheckCircle className="w-4 h-4 text-[#5fc500] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {!policy.comingSoon && (
                    <button
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white rounded-xl font-semibold hover:shadow-lg transition-all group-hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(policy.path);
                      }}
                    >
                      Solicitar Ahora
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PoliciesDashboard;

